/**
 * پنل اندیکاتور
 * ==============
 * هر اندیکاتوری که زیر نمودار قیمت پنل جدا می‌خواهد (RSI، مکدی،
 * استوکاستیک، ATR، OBV، CCI، ویلیامز، MFI) با همین یک کامپوننت ساخته
 * می‌شود.
 *
 * چرا جدا شد: قبلاً RSI و MACD هرکدام کد ساخت و پاک‌سازی خودشان را داشتند.
 * با اضافه‌شدن پنل‌های تازه، آن الگو یعنی هشت بار تکرار همان منطق — و هشت
 * جا برای جاماندنِ یک `remove()` و نشتی حافظه. حالا چرخه عمرِ پنل یک‌جاست:
 * ساخته می‌شود، به نمودار اصلی گره می‌خورد، و با خروج، خودش و اشتراکش را
 * جمع می‌کند.
 *
 * ریاضیات هیچ‌کدام اینجا نیست؛ همه از `lib/indicators.ts` می‌آید که جداگانه
 * با مرجع عددی آزموده شده است.
 */
import { useEffect, useRef } from 'react'
import { createChart, ColorType, IChartApi, ISeriesApi } from 'lightweight-charts'
import {
  Candle, atr, cci, macd, mfi, obv, rsi, stoch, toLine, williamsR,
} from '../lib/indicators'

export type PaneKey = 'rsi' | 'macd' | 'stoch' | 'atr' | 'obv' | 'cci' | 'wr' | 'mfi'

const C = {
  grid: 'rgba(255,255,255,.04)', border: 'rgba(255,255,255,.07)', text: '#8B93AD',
  a: '#7C5CFF', b: '#F5C24C', up: '#00E39A', down: '#FF4D6D',
}

export const PANE_META: Record<PaneKey, { label: string; hint: string }> = {
  rsi: { label: 'RSI (۱۴)', hint: 'قدرت نسبی؛ بالای ۷۰ اشباع خرید' },
  macd: { label: 'MACD (۱۲/۲۶/۹)', hint: 'واگرایی همگرایی میانگین‌ها' },
  stoch: { label: 'استوکاستیک (۱۴/۳/۳)', hint: 'جای قیمت در دامنه اخیر' },
  atr: { label: 'ATR (۱۴)', hint: 'اندازه نوسان؛ برای تعیین حد ضرر' },
  obv: { label: 'حجم متوازن (OBV)', hint: 'فشار خرید و فروش از روی حجم' },
  cci: { label: 'CCI (۲۰)', hint: 'فاصله از میانگین؛ ±۱۰۰ مرز روند' },
  wr: { label: 'ویلیامز ٪R (۱۴)', hint: 'اشباع خرید و فروش' },
  mfi: { label: 'جریان نقدینگی (۱۴)', hint: 'RSI با وزن حجم' },
}

/** خط‌های راهنمای هر پنل (مرزهای اشباع) */
const GUIDES: Partial<Record<PaneKey, number[]>> = {
  rsi: [30, 70], stoch: [20, 80], cci: [-100, 100], wr: [-80, -20], mfi: [20, 80],
}

type Props = {
  paneKey: PaneKey
  candles: Candle[]
  main: IChartApi | null
  height: number
  scaleWidth: number
}

export function IndicatorPane({ paneKey, candles, main, height, scaleWidth }: Props) {
  const box = useRef<HTMLDivElement>(null)
  const chart = useRef<IChartApi | null>(null)
  const lines = useRef<Record<string, ISeriesApi<any>>>({})

  /* ساخت پنل و گره‌زدنش به نمودار اصلی */
  useEffect(() => {
    if (!box.current) return
    const c = createChart(box.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: C.text, fontFamily: 'Vazirmatn, sans-serif', attributionLogo: false,
      },
      grid: { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      rightPriceScale: { borderColor: C.border },
      timeScale: { borderColor: C.border, visible: false },
      crosshair: { vertLine: { color: 'rgba(124,92,255,.5)' }, horzLine: { visible: false } },
      localization: { locale: 'fa-IR' },
      autoSize: true,
    } as any)
    chart.current = c

    if (paneKey === 'macd') {
      lines.current.hist = c.addHistogramSeries({ priceLineVisible: false })
      lines.current.line = c.addLineSeries({ color: C.a, lineWidth: 2, priceLineVisible: false })
      lines.current.signal = c.addLineSeries({ color: C.b, lineWidth: 1, priceLineVisible: false })
    } else if (paneKey === 'stoch') {
      lines.current.k = c.addLineSeries({ color: C.a, lineWidth: 2, priceLineVisible: false })
      lines.current.d = c.addLineSeries({ color: C.b, lineWidth: 1, priceLineVisible: false })
    } else if (paneKey === 'obv') {
      lines.current.main = c.addAreaSeries({
        lineColor: C.a, topColor: 'rgba(124,92,255,.25)', bottomColor: 'rgba(124,92,255,0)',
        lineWidth: 2, priceLineVisible: false,
      })
    } else {
      lines.current.main = c.addLineSeries({ color: C.a, lineWidth: 2, priceLineVisible: false })
    }

    // خط‌های راهنما روی نخستین سری بسته می‌شوند
    const host = lines.current.main ?? lines.current.k ?? lines.current.line
    for (const lv of GUIDES[paneKey] ?? []) {
      try {
        host?.createPriceLine({
          price: lv, color: 'rgba(255,255,255,.18)', lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: '',
        } as any)
      } catch { /* بی‌اهمیت */ }
    }

    // هم‌زمان‌سازی دوطرفه با نمودار اصلی
    let unlink = () => { /* شاید نمودار اصلی هنوز نیامده */ }
    if (main) {
      const a = main.timeScale()
      const b = c.timeScale()
      // ضد حلقه بی‌پایان، بدون «قفل مشترک».
      //
      // نسخه قبلی یک پرچم مشترک بین همه پنل‌ها داشت: وقتی کاربر پنل اول را
      // جابه‌جا می‌کرد، پرچم بالا می‌رفت، نمودار اصلی همان لحظه شنونده‌های
      // خودش را صدا می‌زد و به‌روزرسانیِ پنل دوم پشت همان پرچم می‌ماند —
      // یعنی با دو پنلِ باز، یکی همیشه عقب می‌ماند. حالا به‌جای قفل، پیش از
      // نوشتن مقایسه می‌کنیم: اگر بازه از قبل همان است، چیزی نمی‌نویسیم و
      // حلقه خودبه‌خود می‌ایستد.
      // یک قفلِ **مخصوص همین پنل** (نه مشترک بین پنل‌ها) به‌عنوان کمربند
      // ایمنی: اگر کتابخانه بازه را کمی تصحیح کند، مقایسه هرگز برابر
      // نمی‌شود و دو نمودار می‌توانستند بی‌نهایت بار همدیگر را حرکت بدهند.
      let busy = false
      const sync = (to: IChartApi) => (range: any) => {
        if (busy || !range) return
        busy = true
        try {
          const cur = to.timeScale().getVisibleLogicalRange()
          if (cur && Math.abs(cur.from - range.from) < 0.01
                  && Math.abs(cur.to - range.to) < 0.01) return
          to.timeScale().setVisibleLogicalRange(range)
        } catch { /* هنوز آماده نیست */ } finally { busy = false }
      }
      const toChild = sync(c)
      const toParent = sync(main)
      a.subscribeVisibleLogicalRangeChange(toChild)
      b.subscribeVisibleLogicalRangeChange(toParent)
      try {
        const r = a.getVisibleLogicalRange()
        if (r) b.setVisibleLogicalRange(r)
      } catch { /* هنوز داده‌ای نیست */ }
      unlink = () => {
        try { a.unsubscribeVisibleLogicalRangeChange(toChild) } catch { /* بسته شده */ }
        try { b.unsubscribeVisibleLogicalRangeChange(toParent) } catch { /* بسته شده */ }
      }
    }

    return () => {
      unlink()
      try { c.remove() } catch { /* از قبل بسته شده */ }
      chart.current = null
      lines.current = {}
    }
    // `height` و `scaleWidth` عمداً اینجا نیستند: با هر چرخش گوشی یا باز و
    // بسته شدن نوار ترسیم عوض می‌شوند و پنل را از نو می‌ساختند. اندازه را
    // خودِ `autoSize` از ظرف می‌خواند و عرض محور در افکت بعدی اعمال می‌شود.
  }, [paneKey, main])

  useEffect(() => {
    try {
      chart.current?.applyOptions({
        rightPriceScale: { minimumWidth: scaleWidth },
      } as any)
    } catch { /* بسته شده */ }
    // `main` هم لازم است: با آمدنِ نمودار اصلی، پنل از نو ساخته می‌شود و
    // عرض محور باید دوباره روی نمونه تازه اعمال شود.
  }, [scaleWidth, main])

  /* ریختن داده */
  useEffect(() => {
    if (!candles.length || !chart.current) return
    const closes = candles.map((c) => c.close)
    const put = (k: string, v: any[]) => {
      try { lines.current[k]?.setData(v as any) } catch { /* سری رفته */ }
    }
    if (paneKey === 'rsi') put('main', toLine(candles, rsi(closes, 14)))
    else if (paneKey === 'atr') put('main', toLine(candles, atr(candles, 14)))
    else if (paneKey === 'obv') put('main', toLine(candles, obv(candles)))
    else if (paneKey === 'cci') put('main', toLine(candles, cci(candles, 20)))
    else if (paneKey === 'wr') put('main', toLine(candles, williamsR(candles, 14)))
    else if (paneKey === 'mfi') put('main', toLine(candles, mfi(candles, 14)))
    else if (paneKey === 'stoch') {
      const s = stoch(candles, 14, 3, 3)
      put('k', toLine(candles, s.k))
      put('d', toLine(candles, s.d))
    } else if (paneKey === 'macd') {
      const m = macd(closes, 12, 26, 9)
      put('line', toLine(candles, m.line))
      put('signal', toLine(candles, m.signal))
      put('hist', toLine(candles, m.hist).map((p) => ({
        ...p, color: p.value >= 0 ? 'rgba(0,227,154,.45)' : 'rgba(255,77,109,.45)',
      })))
    }
  }, [candles, paneKey])

  return (
    <div className="shrink-0 border-t border-white/[.06]">
      <div className="px-3 pt-1 text-[10px] text-slate-500">{PANE_META[paneKey].label}</div>
      <div ref={box} style={{ height }} className="w-full" />
    </div>
  )
}
